import { EVENT_TYPE } from '@atlaskit/editor-common/analytics/types/enums';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types/next-editor-plugin';
import type { AnalyticsPlugin } from '@atlaskit/editor-plugin-analytics/analyticsPluginType';
import type { AiStreamingOrchestratorPlugin } from '@atlassian/editor-plugin-ai-streaming-orchestrator/types';
import type { EditorAIAnalyticEventPayload } from '@atlassian/editor-plugin-ai/analytics';

import type { InitAction } from '../stream-processors/partial-stream-parser';

export type UploadAndInsertMediaErrorPayload = {
	action: 'error';
	actionSubject: 'editorPluginAI';
	actionSubjectId: 'uploadAndInsertMedia';
	attributes: {
		conversationChannelId?: string;
		errorFlagShown?: boolean;
		errorMessage: string;
		experienceName: string | null;
		selectionCategory: null;
		selectionType: null;
		source: { isCreateAction: false; type: 'orchestrator' };
		streamId: null;
	};
	eventType: typeof EVENT_TYPE.OPERATIONAL;
};

export const createUploadAndInsertMediaErrorPayload = ({
	errorMessage,
	conversationChannelId,
	errorFlagShown,
}: {
	conversationChannelId?: string;
	errorFlagShown?: boolean;
	errorMessage: string;
}): UploadAndInsertMediaErrorPayload => ({
	action: 'error',
	actionSubject: 'editorPluginAI',
	actionSubjectId: 'uploadAndInsertMedia',
	eventType: EVENT_TYPE.OPERATIONAL,
	attributes: {
		errorMessage,
		...(conversationChannelId ? { conversationChannelId } : {}),
		...(errorFlagShown !== undefined ? { errorFlagShown } : {}),
		experienceName: null,
		selectionType: null,
		selectionCategory: null,
		streamId: null,
		source: { type: 'orchestrator', isCreateAction: false },
	},
});

export function fireUploadAndInsertMediaErrorAnalyticsEvent({
	editorApi,
	errorMessage,
	conversationChannelId,
	errorFlagShown,
}: {
	conversationChannelId?: string;
	editorApi: PublicPluginAPI<[AnalyticsPlugin]>;
	errorFlagShown?: boolean;
	errorMessage: string;
}): void {
	editorApi.analytics?.actions.fireAnalyticsEvent<
		EditorAIAnalyticEventPayload | UploadAndInsertMediaErrorPayload,
		'customEventType'
	>(
		createUploadAndInsertMediaErrorPayload({ errorMessage, conversationChannelId, errorFlagShown }),
	);
}

/** Closed set of violation events, so a new call site cannot invent an unrecognised name. */
export type StreamingContractViolation =
	| 'contract_violation.v2_envelope_malformed'
	| 'contract_violation.partial_after_complete'
	| 'v2_stale_sequence';

/**
 * Every field that may be attached to a streaming-contract violation event.
 *
 * Deliberately a closed set of non-user-generated primitives rather than an open record. The v2
 * envelope's `arguments` field carries the model's generated document content, so an open type
 * would let a future call site put user content into telemetry with nothing failing at compile
 * time. Widening what leaves the browser should require editing this type - that edit is the
 * review checkpoint.
 */
export type StreamingContractViolationData = {
	/** Envelope id. Backend-generated correlation key, not user content. */
	id?: string;
	latestSequence?: number;
	/**
	 * Tool-call name, e.g. `appendNodeToEnd`. Not narrowed to the known names on purpose: the
	 * `non_streamable_name` violation exists to report a name we did not expect.
	 */
	name?: string;
	reason?: 'malformed_envelope' | 'non_streamable_name';
	receivedSequence?: number;
	sequence?: number;
	status?: 'partial' | 'complete';
	streamingContract: 'v2';
};

/**
 * Reports a v2 streaming-contract violation. These were previously recorded only through `log()`,
 * which writes to the console when `window.__aifcDebuggerEnabled__` is set, so none reached
 * telemetry - despite two of the four branches dropping the envelope outright.
 */
export function fireStreamingContractViolationAnalyticsEvent(
	editorApi: PublicPluginAPI<[AnalyticsPlugin, AiStreamingOrchestratorPlugin]>,
	reason: StreamingContractViolation,
	metadata: StreamingContractViolationData,
): void {
	editorApi.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>({
		action: 'error',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'adfStreaming',
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			errorMessage: `Streaming contract violation: ${reason}`,
			errorData: metadata,
			experienceName: null,
			selectionType: null,
			selectionCategory: null,
			streamId: null,
			source: { type: 'orchestrator', isCreateAction: false },
		},
	});
}

export function fireInfiniteLoopDetectedAnalyticsEvent(
	editorApi: PublicPluginAPI<[AnalyticsPlugin, AiStreamingOrchestratorPlugin]>,
	result: InitAction,
): void {
	editorApi.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>({
		action: 'error',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'adfStreaming',
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			errorMessage: 'Infinite loop detected',
			errorData: {
				action: result.name,
				localId: result.localId,
			},
			experienceName: null,
			selectionType: null,
			selectionCategory: null,
			streamId: null,
			source: { type: 'orchestrator', isCreateAction: false },
		},
	});
}

/**
 * Fire an operational analytics event when a v2 tool call cannot be initialised
 * (unknown `name` or missing required arguments) and is dropped by the FIFO
 * scheduler. Mirrors {@link fireInfiniteLoopDetectedAnalyticsEvent}'s shape.
 */
export function fireUnsupportedToolCallAnalyticsEvent(
	editorApi: PublicPluginAPI<[AnalyticsPlugin, AiStreamingOrchestratorPlugin]>,
	{ envelopeId, toolCallName }: { envelopeId: string; toolCallName: string },
): void {
	editorApi.analytics?.actions.fireAnalyticsEvent<EditorAIAnalyticEventPayload, 'customEventType'>({
		action: 'error',
		actionSubject: 'editorPluginAI',
		actionSubjectId: 'adfStreaming',
		eventType: EVENT_TYPE.OPERATIONAL,
		attributes: {
			errorMessage: 'Unsupported v2 tool call',
			errorData: {
				envelopeId,
				toolCallName,
			},
			experienceName: null,
			selectionType: null,
			selectionCategory: null,
			streamId: null,
			source: { type: 'orchestrator', isCreateAction: false },
		},
	});
}

/** Per-event attributes for `parallelToolCall.forceFinished`. */
export type ParallelToolCallForceFinishedAttributes = {
	completedAfterTimeout: boolean;
	envelopeId: string;
	firstLateChunkDelayMs: number | null;
	lastLateChunkDelayMs: number | null;
	lateChunkCount: number;
	timeoutMs: number;
	/** The `name` field from the originating v2 envelope (e.g. `'appendNodeToEnd'`). */
	toolCallName: string;
};

/** Analytics payload for each v2 tool-call force-finished by the FIFO queue timeout. */
export type ParallelToolCallForceFinishedPayload = {
	action: 'forceFinished';
	actionSubject: 'editorPluginAI';
	actionSubjectId: 'parallelToolCall';
	attributes: ParallelToolCallForceFinishedAttributes;
	eventType: typeof EVENT_TYPE.OPERATIONAL;
};

export const createParallelToolCallForceFinishedPayload = (
	attributes: ParallelToolCallForceFinishedAttributes,
): ParallelToolCallForceFinishedPayload => ({
	action: 'forceFinished',
	actionSubject: 'editorPluginAI',
	actionSubjectId: 'parallelToolCall',
	eventType: EVENT_TYPE.OPERATIONAL,
	attributes,
});

export function fireParallelToolCallForceFinishedAnalyticsEvent(
	editorApi: PublicPluginAPI<[AnalyticsPlugin]>,
	attributes: ParallelToolCallForceFinishedAttributes,
): void {
	editorApi.analytics?.actions.fireAnalyticsEvent<
		EditorAIAnalyticEventPayload | ParallelToolCallForceFinishedPayload,
		'customEventType'
	>(createParallelToolCallForceFinishedPayload(attributes));
}
